import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import store from "../../State/Store/store";
import { isSuperTurbo } from "../../utils/speedHelpers";

// ── Super-turbo pot sound dedup ─────────────────────────────────────────────
// In super turbo, all bolts fire simultaneously but arrive over ~400ms.
// Per-frame dedup doesn't work since arrivals stagger across frames.
// Instead, suppress duplicate pot sounds within a 500ms window.
const _potSoundLastPlayed = {};

function _playPotSoundOnce(soundName) {
  const now = performance.now();
  const last = _potSoundLastPlayed[soundName] || 0;
  const delta = now - last;
  if (delta < 1000) {

    return;
  }
  _potSoundLastPlayed[soundName] = now;

  soundManager.play(soundName);
}

// ─── POT Type → Skin / Bone Mapping ─────────────────────────────────────────

/**
 * Maps pot type strings to pots.json skin names and game_view bone names.
 *
 *   "collect" (Blue Zeus)  → skin "collect", bone "attachment_collect_pot"
 *   "double"  (Green Zeus) → skin "double",  bone "attachment_double_pot"
 *   "mystery" (Red Zeus)   → skin "mystery", bone "attachment_mystery_pot"
 */
const POT_CONFIG = {
  collect: { skin: "collect", bone: "attachment_collect_pot" },
  double:  { skin: "double",  bone: "attachment_double_pot" },
  mystery: { skin: "mystery", bone: "attachment_mystery_pot" },
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_STAGE = 11;

// ─── PotController ───────────────────────────────────────────────────────────

/**
 * PotController — manages a single POT visual at a game_view bone position.
 *
 * Renders the matching pots.json spine with the appropriate skin (collect/double/mystery)
 * and drives a growth state machine (API stages 0–11, animations 1–11).
 *
 * Stage mapping: API stages 0 and 1 both use animation 1.
 * Stages 2–11 map directly (API stage N → animation N).
 *
 * Animation naming conventions in pots.json:
 *   - idle/state_N      — looping idle for stage N
 *   - in/state_N        — transition burst INTO stage N (played once)
 *   - collect/state_N   — pulse when trail arrives at POT while at stage N
 *   - anticipation/stage_N — reel anticipation
 *   - blitz_anticipation/stage_N — blitz teaser
 *   - trigger/stage_N   — bonus trigger burst
 *   - win/state_N       — win celebration
 *   - off               — empty pot (stage 0 before any charging)
 *
 * Missing animations fall back to the nearest lower available stage.
 *
 * Lifecycle:
 *   - Created by GameViewController.initializePots()
 *   - Container is addChild'd to game_view spine
 *   - Position synced per-frame via afterUpdateWorldTransforms → _syncBoneToContainer
 *   - setStage(n) jumps to a stage (init/restore)
 *   - setStage(n) grows to a new stage (collect→in→idle chain)
 *   - playHit() plays bolt impact flash (no stage change)
 *   - destroy() cleans up spine + container
 */
class PotController {
  /**
   * @param {string} potType - "collect" | "double" | "mystery"
   * @param {object} gameViewSpine - The game_view Spine instance (for bone lookup)
   */
  constructor(potType, gameViewSpine) {
    const config = POT_CONFIG[potType];
    if (!config) {
      return;
    }

    this.potType = potType;
    this.skinName = config.skin;
    this.boneName = config.bone;
    this.spine = null;
    this.bone = null;
    this.stage = 0;

    // Container that gets synced to bone position every frame
    // zIndex is resolved dynamically from the bone's slot position in draw order
    // (just before its attachment slot, so reel frame renders on top)
    this.container = new PIXI.Container();
    this.container.name = `pot_${potType}`;

    this._createSpine();
    this._findBone(gameViewSpine);

    // Resolve zIndex from the attachment slot's draw-order position
    const attachSlotName = `attachment_${potType}_pot`;
    const attachSlot = gameViewSpine?.skeleton?.findSlot(attachSlotName);
    if (attachSlot) {
      const slotIndex = gameViewSpine.skeleton.slots.indexOf(attachSlot);
      this.container.zIndex = slotIndex - 0.5; // Just before the attachment slot
    } else {
      this.container.zIndex = 0; // Fallback
    }
  }

  // ── Spine Creation ────────────────────────────────────────────────────────

  /**
   * Create a pots.json spine instance with the appropriate skin.
   */
  _createSpine() {
    const assets = PIXI.Assets.cache;
    const skeletonAlias = "pots-skeleton";
    const atlasAlias = "pots-atlas";

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      return;
    }

    try {
      this.spine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
        autoUpdate: true, // POTs idle independently, no manual tick needed
      });

      // Apply the correct skin (collect/double/mystery)
      this.spine.skeleton.setSkinByName(this.skinName);
      this.spine.skeleton.setSlotsToSetupPose();

      this.container.addChild(this.spine);
    } catch (e) {
    }
  }

  // ── Bone Lookup ───────────────────────────────────────────────────────────

  /**
   * Find the attachment bone on the game_view spine.
   * @param {object} gameViewSpine - The game_view Spine instance
   */
  _findBone(gameViewSpine) {
    if (!gameViewSpine?.skeleton) return;
    this.bone = gameViewSpine.skeleton.findBone(this.boneName);
    if (!this.bone) {
    }
  }

  // ── Animation Resolver ────────────────────────────────────────────────────

  /**
   * Resolve an animation name with fallback to nearest lower stage.
   *
   * Tries: "{prefix}/{variant}_{stage}", then "{prefix}/{variant}_{stage-1}", etc.
   * Returns null if no animation exists for any stage.
   *
   * @param {string} prefix - e.g. "idle", "in", "collect", "anticipation"
   * @param {number} stage - Target stage (0–11)
   * @param {string} [variant="state"] - "state" or "stage" (naming convention differs per animation category)
   * @returns {string|null} Animation name or null
   */
  _resolveAnim(prefix, stage, variant = "state") {
    if (!this.spine?.skeleton?.data) return null;

    // Animations are 1-indexed: API stages 0 and 1 both use animation 1
    for (let s = Math.max(1, stage); s >= 1; s--) {
      const name = `${prefix}/${variant}_${s}`;
      if (this.spine.skeleton.data.findAnimation(name)) {
        return name;
      }
    }
    return null;
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /**
   * Set the POT to a new stage and play the growth animation chain.
   * Stages are strictly API-driven — this is the only way to change stage.
   *
   * Plays: collect (flash at prev stage) → in (transition) → idle (loop at new stage)
   *
   * @param {number} stage - Target stage from API (0–11)
   */
  setStage(stage) {
    if (stage === this.stage) return;

    if (!this.spine?.state) {
      this.stage = stage;
      return;
    }

    const prevStage = this.stage;
    this.stage = stage;

    // collect flash at previous stage, then transition into new stage
    // in/state_N is designed to play before idle/state_N+1
    // So growing to stage 5 → in/state_4 → idle/state_5
    // Special case: stage 0/1 → in/state_1 → idle/state_1
    const collectAnim = this._resolveAnim("collect", prevStage);
    const inAnim = this._resolveAnim("in", Math.max(1, stage - 1));
    const idleAnim = this._resolveAnim("idle", stage);

    if (isSuperTurbo(store.getState().settings.autoPlaySpeed)) {
      _playPotSoundOnce(SOUND_NAME.pot_in);
    } else {
      soundManager.play(SOUND_NAME.pot_in);
    }
    if (collectAnim) {
      this.spine.state.setAnimation(0, collectAnim, false);
      if (inAnim) this.spine.state.addAnimation(0, inAnim, false, 0);
      if (idleAnim) this.spine.state.addAnimation(0, idleAnim, true, 0);
    } else if (inAnim) {
      this.spine.state.setAnimation(0, inAnim, false);
      if (idleAnim) this.spine.state.addAnimation(0, idleAnim, true, 0);
    } else if (idleAnim) {
      this.spine.state.setAnimation(0, idleAnim, true);
    } else {
      this.playIdle();
    }
  }

  /**
   * Play bolt impact flash at the current stage — no stage change.
   * Purely visual: bolts are cosmetic, stages are API-driven.
   * Skips if a growth chain is mid-animation (setStage already includes the flash).
   */
  playHit() {
    if (!this.spine?.state) return;
    // Don't interrupt an in-progress growth chain
    const current = this.spine.state.getCurrent(0);
    const currentName = current?.animation?.name || "";
    if (currentName.startsWith("collect/") || currentName.startsWith("in/")) {
      return;
    }
    const collectAnim = this._resolveAnim("collect", this.stage);
    const idleAnim = this._resolveAnim("idle", this.stage);
    if (isSuperTurbo(store.getState().settings.autoPlaySpeed)) {
      _playPotSoundOnce(SOUND_NAME.pot_collect);
    } else {
      soundManager.play(SOUND_NAME.pot_collect);
    }
    if (collectAnim) {
      this.spine.state.setAnimation(0, collectAnim, false);
      if (idleAnim) this.spine.state.addAnimation(0, idleAnim, true, 0);
    }
  }

  /**
   * Play entrance animation for the current stage: in → idle.
   * Used on game load after the intro popup closes to give pots a dramatic entrance.
   * Falls back to playIdle() if no "in" animation exists for the current stage.
   */
  playEntrance() {
    if (!this.spine?.state) return;

    // Ensure colored skin is active (may have been deactivated during bonus)
    this.spine.skeleton.setSkinByName(this.skinName);
    this.spine.skeleton.setSlotsToSetupPose();

    // in/state_N plays before idle/state_N+1
    const inAnim = this._resolveAnim("in", Math.max(1, this.stage - 1));
    const idleAnim = this._resolveAnim("idle", this.stage);

    if (inAnim) {
      this.spine.state.setAnimation(0, inAnim, false);
      if (idleAnim) {
        this.spine.state.addAnimation(0, idleAnim, true, 0);
      }
    } else if (idleAnim) {
      this.spine.state.setAnimation(0, idleAnim, true);
    }
  }

  /**
   * Deactivate the POT — switch to default skin with static empty_pot cloud.
   * Used during bonus game for features that are NOT active.
   * Stops all animations, shows the neutral purple cloud.
   */
  deactivate() {
    if (!this.spine?.state) return;
    this._deactivated = true;

    this.spine.state.clearTracks();
    this.spine.skeleton.setSkinByName("default");
    this.spine.skeleton.setSlotsToSetupPose();

    // Manually show the empty_pot static image (slots start with no attachment)
    const emptySlot = this.spine.skeleton.findSlot("empty_pot");
    if (emptySlot) {
      const attachment = this.spine.skeleton.getAttachmentByName("empty_pot", "empty_pot");
      emptySlot.setAttachment(attachment);
    }
  }

  /**
   * Reactivate the POT — restore colored skin and play entrance animation.
   * Used when returning from bonus game to base game.
   */
  reactivate() {
    this._deactivated = false;
    this.playEntrance();
  }

  /**
   * Play idle animation for the current stage (looping).
   * @param {number} [mixDuration=0] - Crossfade duration in seconds (0 = instant cut)
   */
  playIdle(mixDuration = 0) {
    if (!this.spine?.state) return;

    const anim = this._resolveAnim("idle", this.stage);
    if (anim) {
      const entry = this.spine.state.setAnimation(0, anim, true);
      if (mixDuration > 0 && entry) {
        entry.mixDuration = mixDuration;
      }
    } else {
      this.spine.state.setAnimation(0, "off", false);
    }
  }



  /**
   * Play anticipation animation, then return to idle.
   */
  playAnticipation() {
    if (!this.spine?.state || this._deactivated) return;

    const anim = this._resolveAnim("anticipation", this.stage);
    if (anim) {
      soundManager.play(SOUND_NAME.pot_anticipation);
      this.spine.state.setAnimation(0, anim, false);
      const idle = this._resolveAnim("idle", this.stage);
      if (idle) {
        this.spine.state.addAnimation(0, idle, true, 0);
      }
    }
  }

  /**
   * Play blitz teaser animation, then return to idle.
   */
  playBlitz() {
    if (!this.spine?.state || this._deactivated) return;

    const anim = this._resolveAnim("blitz_anticipation", this.stage);
    if (anim) {
      soundManager.play(SOUND_NAME.pot_blitz_anticipation);
      this.spine.state.setAnimation(0, anim, false);
      const idle = this._resolveAnim("idle", this.stage);
      if (idle) {
        this.spine.state.addAnimation(0, idle, true, 0);
      }
    }
  }

  /**
   * Play win celebration animation once, then return to idle.
   * Used during Zeus activation — pot celebrates while bolt fires.
   */
  playWin() {
    if (!this.spine?.state || this._deactivated) return;

    const winAnim = this._resolveAnim("win", this.stage);
    if (winAnim) {
      soundManager.play(SOUND_NAME.pot_win);
      this.spine.state.setAnimation(0, winAnim, false);
      const idle = this._resolveAnim("idle", this.stage);
      if (idle) {
        this.spine.state.addAnimation(0, idle, true, 0);
      }
    }
  }

  /**
   * Play win celebration animation on loop (no auto-return to idle).
   * Used during feature execution — POT keeps celebrating while its feature runs.
   * Call playIdle() explicitly when the feature finishes.
   */
  playWinLoop() {
    if (!this.spine?.state || this._deactivated) return;

    const winAnim = this._resolveAnim("win", this.stage);
    if (winAnim) {
      soundManager.play(SOUND_NAME.pot_win);
      this.spine.state.setAnimation(0, winAnim, true);
    }
  }

  /**
   * Play win with intro transition: win_in → win (loop).
   * Bonus-only — used by OrbActivator during feature activation.
   * Uses animation mixing for a smooth crossfade between phases.
   * Base game callers should continue using playWin()/playWinLoop().
   */
  playWinLoopWithTransition() {
    if (!this.spine?.state || this._deactivated) return;

    const winInAnim = this.spine.skeleton.data.findAnimation("win/win_in")
      ? "win/win_in"
      : null;
    const winAnim = this._resolveAnim("win", this.stage);

    if (winInAnim && winAnim) {
      // win_in (once) → win (loop) with crossfade blending
      soundManager.play(SOUND_NAME.pot_win);
      this.spine.state.setAnimation(0, winInAnim, false);
      const winEntry = this.spine.state.addAnimation(0, winAnim, true, 0);
      if (winEntry) {
        winEntry.mixDuration = 0.15; // Smooth blend from win_in into win loop
      }
    } else if (winAnim) {
      // Fallback: no win_in available — direct win loop
      this.spine.state.setAnimation(0, winAnim, true);
    }
  }

  /**
   * Transition from win loop back to idle: win_out → idle (loop).
   * Bonus-only — used by OrbActivator when feature finishes.
   * Uses animation mixing for a smooth crossfade between phases.
   * Base game callers should continue using playIdle().
   */
  playIdleFromWin() {
    if (!this.spine?.state || this._deactivated) return;

    const winOutAnim = this.spine.skeleton.data.findAnimation("win/win_out")
      ? "win/win_out"
      : this.spine.skeleton.data.findAnimation("win/win_in")
        ? "win/win_in"
        : null;
    const idleAnim = this._resolveAnim("idle", this.stage);

    if (winOutAnim && idleAnim) {
      // Blend from win loop into win_out transition
      const outEntry = this.spine.state.setAnimation(0, winOutAnim, false);
      if (outEntry) {
        outEntry.mixDuration = 0.2; // Smooth blend from win loop into out transition
      }
      // Then blend from win_out into idle loop
      const idleEntry = this.spine.state.addAnimation(0, idleAnim, true, 0);
      if (idleEntry) {
        idleEntry.mixDuration = 0.15; // Smooth blend from out transition into idle
      }
    } else if (idleAnim) {
      // Fallback: no win_out available — crossfade directly into idle
      const entry = this.spine.state.setAnimation(0, idleAnim, true);
      if (entry) {
        entry.mixDuration = 0.2;
      }
    } else {
      this.spine.state.setAnimation(0, "off", false);
    }
  }

  /**
   * Play trigger animation (bonus entrance) → win celebration.
   *
   * Trigger animations use ranged naming: trigger/stage_N_to_11
   * Available brackets: 1, 5, 10
   *   - Stages 0–1    → trigger/stage_1_to_11
   *   - Stages 2–5    → trigger/stage_5_to_11
   *   - Stages 6–10   → trigger/stage_10_to_11
   *
   * After trigger completes, POT is at final stage (GDD §12.2).
   */
  playTrigger({ silent = false } = {}) {
    if (!this.spine?.state) return;

    // Stage 0 and 1 both use animation 1 — same alias as _resolveAnim()
    const effectiveStage = Math.max(1, this.stage);

    // Find the highest bracket ≤ current stage (animations are 1-indexed)
    const brackets = [10, 5, 1];
    let triggerAnim = null;

    for (const bracket of brackets) {
      if (effectiveStage >= bracket) {
        const name = `trigger/stage_${bracket}_to_${MAX_STAGE}`;
        if (this.spine.skeleton.data.findAnimation(name)) {
          triggerAnim = name;
          break;
        }
      }
    }


    if (triggerAnim) {
      if (!silent) soundManager.play(SOUND_NAME.pot_trigger);
      this.spine.state.setAnimation(0, triggerAnim, false);

      // Play pot_win when trigger animation completes (not immediately)
      const winAnim = this._resolveAnim("win", MAX_STAGE);
      if (winAnim) {
        this.spine.state.addAnimation(0, winAnim, true, 0);
        if (!silent) {
          const listener = {
            complete: (entry) => {
              if (entry.animation.name === triggerAnim) {
                soundManager.play(SOUND_NAME.pot_win);
                this.spine.state.removeListener(listener);
              }
            },
          };
          this.spine.state.addListener(listener);
        }
      }
    }

    // POT is now at final stage
    this.stage = MAX_STAGE;
  }


  /**
   * Reset POT to stage 0 and play "off" animation.
   * Called after bonus game ends.
   */
  reset() {
    this.stage = 0;
    if (!this.spine?.state) return;
    this.spine.state.setAnimation(0, "off", false);
  }

  /**
   * Update the game_view spine reference (e.g., after resize re-creation).
   * @param {object} gameViewSpine
   */
  updateSpineRef(gameViewSpine) {
    this._findBone(gameViewSpine);
  }

  /**
   * Clean up spine and container.
   */
  destroy() {

    if (this.spine && !this.spine.destroyed) {
      this.spine.destroy();
    }
    this.spine = null;

    if (this.container && !this.container.destroyed) {
      this.container.destroy();
    }
    this.container = null;
    this.bone = null;
  }
}

export default PotController;
export { POT_CONFIG };
